import { useState, useMemo, useEffect } from 'react'
import { generateClient } from 'aws-amplify/api'
import './Efficiencies.css'

function Efficiencies({ data, companyId, userEmail }) {
  const [threadsByPool, setThreadsByPool] = useState({ coaching: 0, mediation: 0, hrEscalation: 0, total: 0 })
  const [loading, setLoading] = useState(true)
  const [assumptions, setAssumptions] = useState({
    tribunalCost: 50000,
    recruitmentCost: 5000,
    managerAnnualWage: 50000, // Annual wage instead of hourly rate
    mediationSessionCost: 300,
    legalFeeEstimate: 2000,
    coachingHoursPerIssue: 1,
    mediationHoursPerIssue: 2
  })
  const [editingAssumption, setEditingAssumption] = useState(null)
  const [tempValue, setTempValue] = useState('')
  const [mediationCardFlipped, setMediationCardFlipped] = useState(false)
  const [hrCardFlipped, setHrCardFlipped] = useState(false)

  const client = useMemo(() => {
    try {
      return generateClient()
    } catch (error) {
      console.error('Error initializing GraphQL client:', error)
      return null
    }
  }, [])

  useEffect(() => {
    const fetchThreadsByPool = async () => {
      if (!client || !companyId) {
        setLoading(false)
        return
      }
      
      try {
        setLoading(true)
        const { getThreadsByPool } = await import('../graphql/queries')
        
        if (!userEmail) {
          console.error('No user email found')
          setLoading(false)
          return
        }
        
        const response = await client.graphql({
          query: getThreadsByPool,
          variables: { companyId: companyId, userEmail: userEmail }
        })
        
        if (response.errors) {
          console.error('Error fetching threads by pool:', response.errors)
          return
        }
        
        if (response.data?.getThreadsByPool) {
          setThreadsByPool(response.data.getThreadsByPool)
        }
      } catch (err) {
        console.error('Error fetching threads by pool:', err)
      } finally {
        setLoading(false)
      }
    }
    
    fetchThreadsByPool()
  }, [client, companyId, userEmail])

  // Calculate value delivered (all time, not just quarter)
  const calculations = useMemo(() => {
    const { coaching, mediation, hrEscalation } = threadsByPool

    // Calculate manager hourly rate from annual wage
    // 37 hours per week, 52 weeks = 1,924 hours
    // 28 days leave = 28 days / 5 days per week * 37 hours = 5.6 weeks * 37 = 207.2 hours
    // Working hours per year = 1,924 - 207.2 = 1,716.8 hours
    const workingHoursPerYear = (52 * 37) - (28 / 5 * 37) // 1,716.8 hours
    const managerHourlyRate = assumptions.managerAnnualWage / workingHoursPerYear

    // Calculate values
    // Coaching: Time saved (configurable hours per issue)
    const coachingHoursSaved = coaching * assumptions.coachingHoursPerIssue // Actual hours saved
    const coachingValue = coachingHoursSaved * managerHourlyRate // Monetary value

    // Mediation: Time saved (configurable hours per issue - more complex than coaching)
    const mediationHoursSaved = mediation * assumptions.mediationHoursPerIssue // Actual hours saved
    const mediationTimeValue = mediationHoursSaved * managerHourlyRate // Monetary value of time saved
    // Mediation: Cost avoidance (tribunal cost vs mediation session cost)
    // Only 6% of mediations would have escalated to legal action (midpoint of 2-10% range)
    const mediationsThatWouldEscalate = mediation * 0.06
    const mediationCostAvoidance = mediationsThatWouldEscalate * (assumptions.tribunalCost - assumptions.mediationSessionCost)
    // Mediation: Churn prevention (20% of mediations would result in employee churn if unresolved - midpoint of 10-30% range)
    const mediationsThatWouldChurn = mediation * 0.20
    const mediationChurnPrevention = mediationsThatWouldChurn * assumptions.recruitmentCost
    // Mediation: Actual cost of mediation sessions
    const mediationSessionCosts = mediation * assumptions.mediationSessionCost
    const mediationValue = mediationCostAvoidance + mediationTimeValue + mediationChurnPrevention - mediationSessionCosts // Total mediation value (benefits minus costs)

    // HR Escalation calculations
    // Only 6% of HR escalations would have escalated to legal action (midpoint of 2-10% range)
    const hrEscalationsThatWouldEscalate = hrEscalation * 0.06
    const hrLegalCostAvoidance = hrEscalationsThatWouldEscalate * assumptions.tribunalCost
    // 20% of HR escalations would result in employee churn if unresolved (midpoint of 10-30% range)
    const hrEscalationsThatWouldChurn = hrEscalation * 0.20
    const hrChurnPrevention = hrEscalationsThatWouldChurn * assumptions.recruitmentCost
    // HR time cost: 2 hours per case
    const hrTimeCost = hrEscalation * 2 * managerHourlyRate
    const hrEscalationValue = hrLegalCostAvoidance + hrChurnPrevention - hrTimeCost // Net value (benefits minus HR time cost)

    const costAvoidance = mediationCostAvoidance + hrLegalCostAvoidance
    const timeSavedHours = coachingHoursSaved + mediationHoursSaved // Hours saved (for display) - both coaching and mediation
    const timeSavedValue = coachingValue + mediationTimeValue // Monetary value (for total calculation)
    const retentionValue = hrChurnPrevention

    // Hero total is simply the sum of all three impact cards to ensure alignment
    const totalValue = coachingValue + mediationValue + hrEscalationValue

    return {
      totalValue,
      costAvoidance,
      timeSavedHours, // Hours for display
      timeSavedValue, // Monetary value for calculations
      retentionValue,
      byStage: {
        coaching: { count: coaching, value: coachingValue },
        mediation: { count: mediation, value: mediationValue },
        hrEscalation: { count: hrEscalation, value: hrEscalationValue }
      }
    }
  }, [threadsByPool, assumptions])

  const handleAssumptionEdit = (key) => {
    setEditingAssumption(key)
    setTempValue(assumptions[key].toString())
  }

  const handleAssumptionSave = (key) => {
    const numValue = parseFloat(tempValue)
    if (!isNaN(numValue) && numValue >= 0) {
      setAssumptions(prev => ({ ...prev, [key]: numValue }))
    }
    setEditingAssumption(null)
    setTempValue('')
  }

  const handleAssumptionCancel = () => {
    setEditingAssumption(null)
    setTempValue('')
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)
  }

  if (loading) {
    return <div className="loading">Loading efficiencies data...</div>
  }

  return (
    <div className="efficiencies-container">
      {/* Top Section: Employees Helped */}
      <div className="efficiencies-section">
        <h2 className="section-title">Total Value Delivered This Quarter</h2>
        <div className="total-value-card">
          <div className="total-value-amount">{formatCurrency(calculations.totalValue)}</div>
          <div className="total-value-label">potential cost savings</div>
        </div>
        <div className="value-breakdown">
          <div className="breakdown-item">
            <div className="breakdown-label">People Helped</div>
            <div className="breakdown-value">{data?.activeUserCount || 0}</div>
          </div>
          <div className="breakdown-item">
            <div className="breakdown-label">Time Saved</div>
            <div className="breakdown-value">{calculations.timeSavedHours} hours</div>
          </div>
          <div className="breakdown-item">
            <div className="breakdown-label">People Retained</div>
            <div className="breakdown-value">{threadsByPool.hrEscalation || 0}</div>
          </div>
        </div>
      </div>

      {/* Middle Section: Where Olivea Made Impact */}
      <div className="efficiencies-section">
        <h2 className="section-title">Where Olivea Made Impact</h2>
        <div className="impact-grid">
          <div className="impact-card">
            <div className="impact-stage">Coaching</div>
            <div className="impact-count">{calculations.byStage.coaching.count} issues</div>
            <div className="impact-value">{formatCurrency(calculations.byStage.coaching.value)}</div>
            <div className="impact-description">
              Early intervention through coaching prevented escalation
            </div>
          </div>
          <div 
            className={`impact-card mediation-card ${mediationCardFlipped ? 'flipped' : ''}`}
            onClick={() => setMediationCardFlipped(!mediationCardFlipped)}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-front">
              <div className="impact-stage">Mediation</div>
              <div className="impact-count">{calculations.byStage.mediation.count} issues</div>
              <div className="impact-value">{formatCurrency(calculations.byStage.mediation.value)}</div>
              <div className="impact-description">
                Tap for more info
              </div>
            </div>
            <div className="card-back">
              <div className="impact-description">
                Mediation resolved conflicts before legal action. Based on industry data: 2-10% of conflicts escalate to legal issues (using 6% midpoint). Additionally, 10-30% of unresolved mediation cases result in employee churn (using 20% midpoint), which we've prevented.
              </div>
            </div>
          </div>
          <div 
            className={`impact-card hr-card ${hrCardFlipped ? 'flipped' : ''}`}
            onClick={() => setHrCardFlipped(!hrCardFlipped)}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-front">
              <div className="impact-stage">HR Escalation</div>
              <div className="impact-count">{calculations.byStage.hrEscalation.count} issues</div>
              <div className="impact-value">{formatCurrency(calculations.byStage.hrEscalation.value)}</div>
              <div className="impact-description">
                Tap for more info
              </div>
            </div>
            <div className="card-back">
              <div className="impact-description">
                Serious issues handled. Based on industry data: 6% would have escalated to legal action, 20% would have resulted in employee churn if unresolved. Includes HR time cost (2 hours per case).
              </div>
            </div>
          </div>
        </div>
        <div className="impact-insight">
          <div className="insight-icon">💡</div>
          <div className="insight-text">
            <strong>Early intervention matters:</strong> Issues caught at the coaching stage save significantly more than those requiring HR escalation.
          </div>
        </div>
      </div>

      {/* Bottom Section: Configurable Assumptions */}
      <div className="efficiencies-section">
        <h2 className="section-title">Configurable Assumptions</h2>
        <p className="section-description">
          Adjust these values to match your industry and company specifics. Changes update calculations in real-time.
        </p>
        <div className="assumptions-grid">
          <div className="assumption-item">
            <label className="assumption-label">Average Tribunal Cost</label>
            {editingAssumption === 'tribunalCost' ? (
              <div className="assumption-edit">
                <input
                  type="number"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  className="assumption-input"
                  min="0"
                  step="1000"
                />
                <button onClick={() => handleAssumptionSave('tribunalCost')} className="assumption-btn save">Save</button>
                <button onClick={handleAssumptionCancel} className="assumption-btn cancel">Cancel</button>
              </div>
            ) : (
              <div className="assumption-display">
                <span className="assumption-value">{formatCurrency(assumptions.tribunalCost)}</span>
                <button onClick={() => handleAssumptionEdit('tribunalCost')} className="assumption-edit-btn">Edit</button>
              </div>
            )}
          </div>

          <div className="assumption-item">
            <label className="assumption-label">Average Recruitment Cost per Role</label>
            {editingAssumption === 'recruitmentCost' ? (
              <div className="assumption-edit">
                <input
                  type="number"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  className="assumption-input"
                  min="0"
                  step="100"
                />
                <button onClick={() => handleAssumptionSave('recruitmentCost')} className="assumption-btn save">Save</button>
                <button onClick={handleAssumptionCancel} className="assumption-btn cancel">Cancel</button>
              </div>
            ) : (
              <div className="assumption-display">
                <span className="assumption-value">{formatCurrency(assumptions.recruitmentCost)}</span>
                <button onClick={() => handleAssumptionEdit('recruitmentCost')} className="assumption-edit-btn">Edit</button>
              </div>
            )}
          </div>

          <div className="assumption-item">
            <label className="assumption-label">Manager Annual Wage</label>
            {editingAssumption === 'managerAnnualWage' ? (
              <div className="assumption-edit">
                <input
                  type="number"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  className="assumption-input"
                  min="0"
                  step="1000"
                />
                <button onClick={() => handleAssumptionSave('managerAnnualWage')} className="assumption-btn save">Save</button>
                <button onClick={handleAssumptionCancel} className="assumption-btn cancel">Cancel</button>
              </div>
            ) : (
              <div className="assumption-display">
                <span className="assumption-value">{formatCurrency(assumptions.managerAnnualWage)}/year</span>
                <button onClick={() => handleAssumptionEdit('managerAnnualWage')} className="assumption-edit-btn">Edit</button>
              </div>
            )}
          </div>

          <div className="assumption-item">
            <label className="assumption-label">Mediation Session Cost</label>
            {editingAssumption === 'mediationSessionCost' ? (
              <div className="assumption-edit">
                <input
                  type="number"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  className="assumption-input"
                  min="0"
                  step="50"
                />
                <button onClick={() => handleAssumptionSave('mediationSessionCost')} className="assumption-btn save">Save</button>
                <button onClick={handleAssumptionCancel} className="assumption-btn cancel">Cancel</button>
              </div>
            ) : (
              <div className="assumption-display">
                <span className="assumption-value">{formatCurrency(assumptions.mediationSessionCost)}</span>
                <button onClick={() => handleAssumptionEdit('mediationSessionCost')} className="assumption-edit-btn">Edit</button>
              </div>
            )}
          </div>

          <div className="assumption-item">
            <label className="assumption-label">Legal Fee Estimate</label>
            {editingAssumption === 'legalFeeEstimate' ? (
              <div className="assumption-edit">
                <input
                  type="number"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  className="assumption-input"
                  min="0"
                  step="100"
                />
                <button onClick={() => handleAssumptionSave('legalFeeEstimate')} className="assumption-btn save">Save</button>
                <button onClick={handleAssumptionCancel} className="assumption-btn cancel">Cancel</button>
              </div>
            ) : (
              <div className="assumption-display">
                <span className="assumption-value">{formatCurrency(assumptions.legalFeeEstimate)}</span>
                <button onClick={() => handleAssumptionEdit('legalFeeEstimate')} className="assumption-edit-btn">Edit</button>
              </div>
            )}
          </div>

          <div className="assumption-item">
            <label className="assumption-label">Coaching Hours per Issue</label>
            {editingAssumption === 'coachingHoursPerIssue' ? (
              <div className="assumption-edit">
                <input
                  type="number"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  className="assumption-input"
                  min="0"
                  step="0.5"
                />
                <button onClick={() => handleAssumptionSave('coachingHoursPerIssue')} className="assumption-btn save">Save</button>
                <button onClick={handleAssumptionCancel} className="assumption-btn cancel">Cancel</button>
              </div>
            ) : (
              <div className="assumption-display">
                <span className="assumption-value">{assumptions.coachingHoursPerIssue} hours</span>
                <button onClick={() => handleAssumptionEdit('coachingHoursPerIssue')} className="assumption-edit-btn">Edit</button>
              </div>
            )}
          </div>

          <div className="assumption-item">
            <label className="assumption-label">Mediation Hours per Issue</label>
            {editingAssumption === 'mediationHoursPerIssue' ? (
              <div className="assumption-edit">
                <input
                  type="number"
                  value={tempValue}
                  onChange={(e) => setTempValue(e.target.value)}
                  className="assumption-input"
                  min="0"
                  step="0.5"
                />
                <button onClick={() => handleAssumptionSave('mediationHoursPerIssue')} className="assumption-btn save">Save</button>
                <button onClick={handleAssumptionCancel} className="assumption-btn cancel">Cancel</button>
              </div>
            ) : (
              <div className="assumption-display">
                <span className="assumption-value">{assumptions.mediationHoursPerIssue} hours</span>
                <button onClick={() => handleAssumptionEdit('mediationHoursPerIssue')} className="assumption-edit-btn">Edit</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Efficiencies

